import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Database, Upload, Wand2, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserStore } from '@/store/userStore';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function LogaTab() {
  const { token } = useUserStore();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);
  const auth = { Authorization: `Bearer ${token}` };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['loga-stats'],
    queryFn: () => axios.get(`${API}/api/loga/stats`, { headers: auth }).then(r => r.data),
  });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/loga/upload`, fd, {
        headers: { ...auth, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${res.data.inserted} insertados, ${res.data.updated} actualizados`);
      qc.invalidateQueries({ queryKey: ['loga-stats'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir LOGA');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const reprocessMut = useMutation({
    mutationFn: (dryRun) => axios.post(
      `${API}/api/loga/reprocess-orphans`,
      { dry_run: dryRun, modules: ['accidentes'] },
      { headers: auth, timeout: 60000 },
    ).then(r => r.data),
    onSuccess: (data, dryRun) => {
      setReprocessResult({ ...data.results.accidentes, dryRun });
      const r = data.results.accidentes;
      toast.success(
        dryRun
          ? `[Dry-run] ${r.matched}/${r.scanned} se mapearían (${(100 * r.matched / Math.max(r.scanned, 1)).toFixed(1)}%)`
          : `${r.matched} accidentes re-mapeados con éxito`,
      );
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error en reprocess'),
  });

  return (
    <div className="space-y-6" data-testid="loga-tab">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #4A9EFF 0%, #00C853 100%)' }}>
          <Database size={18} className="text-white" />
        </div>
        <div>
          <h2 className="text-base font-bold text-white">Mapeo LOGA PRL</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            Maestro de trabajadores Valoriza. Permite mapear accidentes huérfanos a su centro real
            cruzando <code>Número Personal</code> / <code>NIF</code> / <code>Cliente</code> con la oficina de gastos.
          </p>
        </div>
      </div>

      {/* Stats */}
      {statsLoading ? <Skeleton className="h-32 w-full" /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Trabajadores cargados" value={stats?.total_workers || 0} color="#4A9EFF" />
          <StatCard label="Con oficina asignada" value={stats?.workers_with_oficina || 0} color="#00C853" />
          <StatCard label="Sedes (oficinas) distintas" value={stats?.distinct_oficinas || 0} color="#FF9800" />
          <StatCard label="Clientes distintos" value={stats?.distinct_clientes || 0} color="#9C27B0" />
        </div>
      )}

      {/* A2/A4 — Quality audit of LOGA `oficina_gastos` codes */}
      {stats && stats.distinct_oficinas > 0 && (
        <div
          className="rounded-xl p-4"
          data-testid="loga-quality-audit"
          style={{
            background: stats.oficinas_invalidas > 0 ? 'rgba(229,57,53,0.06)' : 'rgba(0,168,71,0.05)',
            border: `1px solid ${stats.oficinas_invalidas > 0 ? 'rgba(229,57,53,0.25)' : 'rgba(0,168,71,0.2)'}`,
          }}
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--vz-text-primary)' }}>
                Calidad del mapeo LOGA
              </h3>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                Códigos de "Oficina de gastos" únicos validados contra <code>centros_catalog</code> y aliases.
              </p>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span style={{ color: '#00A847' }}>✓ {stats.oficinas_validas || 0} válidas</span>
              <span style={{ color: stats.oficinas_invalidas > 0 ? '#E53935' : 'var(--vz-text-muted)' }}>
                ✕ {stats.oficinas_invalidas || 0} inválidas
              </span>
            </div>
          </div>
          {stats.oficinas_invalidas > 0 && stats.oficinas_invalidas_muestra?.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--vz-text-muted)' }}>
                Muestra de códigos inválidos en LOGA (los trabajadores con estos códigos quedan huérfanos al cruzar):
              </p>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {stats.oficinas_invalidas_muestra.map((code) => (
                  <span
                    key={code}
                    className="text-[10px] font-mono px-2 py-0.5 rounded"
                    style={{ background: 'rgba(229,57,53,0.12)', color: '#E53935', border: '1px solid rgba(229,57,53,0.25)' }}
                    title={code}
                  >
                    {code.length > 40 ? code.substring(0, 38) + '…' : code}
                  </span>
                ))}
              </div>
              <p className="text-[10px] mt-2" style={{ color: 'var(--vz-text-muted)' }}>
                💡 Soluciones: corregir el código origen en LOGA PRL, o añadir un alias en <strong>Admin → Mapeo Texto</strong> que apunte al código canónico del centro.
              </p>
            </div>
          )}
        </div>
      )}
      {stats?.last_upload_at && (
        <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>
          Última carga: {new Date(stats.last_upload_at).toLocaleString('es-ES')} por {stats.last_upload_by || '—'}
        </p>
      )}

      {/* Upload */}
      <div className="rounded-xl p-5 space-y-3"
        style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
        <h3 className="text-sm font-semibold flex items-center gap-2 text-white">
          <Upload size={14} /> Subir / actualizar maestro LOGA
        </h3>
        <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>
          Sube el CSV exportado de LOGA PRL. Se hará upsert por <code>Número Personal</code>: los
          trabajadores existentes se actualizan, los nuevos se insertan.
        </p>
        <input ref={fileRef} type="file" accept=".csv" className="hidden"
          onChange={e => upload(e.target.files[0])} data-testid="loga-upload-input" />
        <Button size="sm" className="text-xs gap-1.5" disabled={uploading}
          onClick={() => fileRef.current?.click()} data-testid="loga-upload-btn">
          <Upload size={11} /> {uploading ? 'Subiendo...' : 'Seleccionar CSV'}
        </Button>
      </div>

      {/* Reprocess */}
      <div className="rounded-xl p-5 space-y-3"
        style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
        <h3 className="text-sm font-semibold flex items-center gap-2 text-white">
          <Wand2 size={14} /> Reprocesar accidentes sin centro
        </h3>
        <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>
          Re-aplica el matching a los accidentes huérfanos. Prioridad:
          (1) <code>oficina_gastos</code> Delt@ → catálogo, (2) corrección de alias,
          (3) LOGA por Nº Personal / NIF / Cliente único.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs gap-1.5"
            disabled={reprocessMut.isPending}
            onClick={() => reprocessMut.mutate(true)}
            data-testid="loga-reprocess-dry">
            <RefreshCw size={11} className={reprocessMut.isPending ? 'animate-spin' : ''} />
            Dry-run (vista previa)
          </Button>
          <Button size="sm" className="text-xs gap-1.5"
            disabled={reprocessMut.isPending}
            onClick={() => reprocessMut.mutate(false)}
            data-testid="loga-reprocess-real">
            <Wand2 size={11} /> Aplicar mapeo
          </Button>
        </div>

        {reprocessResult && (
          <div className="mt-3 rounded-lg p-3 text-xs space-y-2"
            style={{ background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.25)' }}>
            <div className="flex flex-wrap gap-3">
              <span><strong style={{ color: '#4CAF50' }}>{reprocessResult.matched}</strong> de {reprocessResult.scanned} mapeados ({(100 * reprocessResult.matched / Math.max(reprocessResult.scanned, 1)).toFixed(1)}%)</span>
              <span style={{ color: '#FF9800' }}>{reprocessResult.unmatched} sin mapear</span>
              {reprocessResult.dryRun && <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#FF9800', color: '#fff' }}>DRY-RUN</span>}
            </div>
            {reprocessResult.by_via && Object.keys(reprocessResult.by_via).length > 0 && (
              <div className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                Por método: {Object.entries(reprocessResult.by_via).map(([k, v]) => `${k}=${v}`).join(' · ')}
              </div>
            )}
            {reprocessResult.unmapped_sample?.length > 0 && (
              <details className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
                <summary className="cursor-pointer">Muestra de {reprocessResult.unmapped_sample.length} sin mapear</summary>
                <ul className="mt-1.5 space-y-0.5 pl-3">
                  {reprocessResult.unmapped_sample.map((s, i) => (
                    <li key={i}>{s.apellidos_nombre} | cliente={s.cliente || '—'} | NIF={s.cif || '—'} | NP={s.n_personal || '—'}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Help */}
      <div className="rounded-xl p-4 text-[11px] flex items-start gap-2"
        style={{ background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.2)' }}>
        <AlertCircle size={14} style={{ color: '#4A9EFF', marginTop: 1 }} className="flex-shrink-0" />
        <div style={{ color: 'var(--vz-text-secondary)' }}>
          <strong className="text-white">¿Cómo funciona?</strong> El parser extrae el código de sede del campo
          <code> Oficina de gastos</code> de Delt@ (ej. <code>7000337.ES.200 → 7000337</code>) y lo busca en el
          catálogo Valoriza. Si no encuentra, prueba con LOGA por trabajador. Los registros sin n_personal
          ni oficina se quedan sin mapear y aparecen en la pestaña <strong>"Sin centro"</strong> del módulo Accidentes.
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="rounded-xl p-4"
      style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
      <div className="text-2xl font-bold" style={{ color }}>{(value || 0).toLocaleString('es-ES')}</div>
      <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'var(--vz-text-muted)' }}>{label}</div>
    </div>
  );
}
